// colDef/departmentColDef.tsx
import {
  ActionDropdown,
  ActionDropdownContent,
  ActionDropdownTrigger,
  DropdownNode,
} from "@/components/ActionDropdown";
import { cn } from "@/lib/utils";
import { DepartmentType } from "@/services/department.service";
import { createColumnHelper } from "@tanstack/react-table";
import { MoreVertical, Pencil, Trash2 } from "lucide-react";
import { Link } from "react-router-dom";

const columnHelper = createColumnHelper<DepartmentType>();

export type DepartmentActionType = {
  id: string;
  name: string;
  action: "edit" | "delete";
};

export type DepartmentPermissions = {
  canUpdate: boolean;
  canDelete: boolean;
};

export const createDepartmentColumns = (
  onActionClick: (payload: DepartmentActionType) => void,
  permissions: DepartmentPermissions
) => [
  // Code — dark rounded badge
  columnHelper.accessor("code", {
    header: "Code",
    cell: (info) => (
      <span className="rounded-md bg-gray-900 px-2.5 py-1 text-xs font-bold text-white">
        {info.getValue()}
      </span>
    ),
  }),

  // Department Name — blue link to view page
  columnHelper.accessor("name", {
    header: "Department Name",
    cell: (info) => (
      <Link
        to={`/department/${info.row.original.id}`}
        className="font-medium hover:underline"
      >
        {info.getValue()}
      </Link>
    ),
  }),

  // HOD — orange/amber link
  columnHelper.accessor("hod_name", {
    header: "HOD",
    cell: (info) => (
      <span className="font-medium">{info.getValue() || "N/A"}</span>
    ),
  }),

  // Established year
  columnHelper.accessor("established_year", {
    header: "Established",
    cell: (info) => <span>{info.getValue()}</span>,
  }),

  // Status pill
  columnHelper.accessor("is_active", {
    header: "Status",
    cell: (info) => {
      const isActive = info.getValue();
      return (
        <span
          className={cn(
            "rounded-full px-3 py-1 text-xs font-semibold",
            isActive ? "bg-gray-900 text-white" : "bg-gray-100 text-gray-500"
          )}
        >
          {isActive ? "Active" : "Inactive"}
        </span>
      );
    },
  }),

  ...(permissions.canUpdate || permissions.canDelete
    ? [
        columnHelper.display({
          id: "actions",
          header: "",
          cell: (info) => {
            const data = info.row.original;
            const id = data.id || "";

            const childrenNodes: DropdownNode[] = [];

            if (permissions.canUpdate) {
              childrenNodes.push({
                type: "item",
                label: "Edit Department",
                icon: <Pencil size={14} />,
                onSelect: () =>
                  onActionClick({ id, name: data.name, action: "edit" }),
              });
            }

            if (permissions.canDelete) {
              childrenNodes.push({
                type: "item",
                label: "Delete Department",
                icon: <Trash2 size={14} />,
                className: "text-red-600 focus:text-red-600",
                iconClass: "text-red-600",
                onSelect: () =>
                  onActionClick({ id, name: data.name, action: "delete" }),
              });
            }

            if (childrenNodes.length === 0) return null;

            const dropdownNodes: DropdownNode[] = [
              {
                type: "group",
                children: childrenNodes,
              },
            ];

            return (
              <ActionDropdown>
                <ActionDropdownTrigger
                  triggerContent={<MoreVertical size={16} />}
                  buttonVariant="ghost"
                  buttonSize="icon"
                  className={{ buttonClass: "h-8 w-8 p-0" }}
                />
                <ActionDropdownContent
                  dropdownShape={dropdownNodes}
                  align="end"
                />
              </ActionDropdown>
            );
          },
        }),
      ]
    : []),
];
